import type { ParsedCSVRow, CSVValidationError } from '../types/asset-import.types';

/**
 * Formats a number as currency (GBP)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

/**
 * Validates a file before processing
 * Checks file type and size
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!file.name.endsWith('.csv')) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a CSV file.',
    };
  }

  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size exceeds 10MB limit.',
    };
  }

  return { valid: true };
}

/**
 * Validates an array of asset import rows
 * Returns an object with valid rows and errors
 */
export function validateAssets(rows: ParsedCSVRow[]): {
  validRows: ParsedCSVRow[];
  errors: CSVValidationError[];
} {
  const validRows: ParsedCSVRow[] = [];
  const errors: CSVValidationError[] = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 2; // +2 because row 1 is header, and we're 0-indexed
    const rowErrors: string[] = [];

    // Validate Asset Code
    if (!row.assetCode || row.assetCode.trim() === '') {
      rowErrors.push('Asset code is required');
    }

    // Validate Description
    if (!row.description || row.description.trim() === '') {
      rowErrors.push('Description is required');
    }

    // Validate Category
    if (!row.category || row.category.trim() === '') {
      rowErrors.push('Category is required');
    }

    // Validate Bought Value
    if (!row.boughtValue) {
      rowErrors.push('Bought value is required');
    } else {
      const value = parseFloat(row.boughtValue.toString().replace(/[£,]/g, ''));
      if (isNaN(value)) {
        rowErrors.push('Invalid number format for bought value');
      } else if (value <= 0) {
        rowErrors.push('Bought value must be greater than zero');
      }
    }

    // Validate Bought Date
    if (!row.boughtDate) {
      rowErrors.push('Bought date is required');
    } else {
      const date = parseDateString(row.boughtDate);
      if (!date || isNaN(date.getTime())) {
        rowErrors.push('Invalid date format. Use DD/MM/YYYY');
      }
    }

    // If there are errors for this row, add them to the errors array
    if (rowErrors.length > 0) {
      rowErrors.forEach(error => {
        errors.push({
          row: rowNumber,
          field: getFieldFromError(error),
          error: error,
        });
      });
    } else {
      // Row is valid
      validRows.push(row);
    }
  });

  return { validRows, errors };
}

/**
 * Parse a date string in DD/MM/YYYY format
 */
function parseDateString(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Try DD/MM/YYYY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date is real
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return date;
    }
  }

  // Try ISO format as fallback
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  return null;
}

/**
 * Extract field name from error message
 */
function getFieldFromError(error: string): string {
  if (error.includes('Asset code') || error.includes('asset code')) {
    return 'Asset Code';
  }
  if (error.includes('Description') || error.includes('description')) {
    return 'Description';
  }
  if (error.includes('Category') || error.includes('category')) {
    return 'Category';
  }
  if (error.includes('Bought value') || error.includes('bought value') || error.includes('value')) {
    return 'Bought Value';
  }
  if (error.includes('Bought date') || error.includes('bought date') || error.includes('date')) {
    return 'Bought Date';
  }
  if (error.includes('Supplier') || error.includes('supplier')) {
    return 'Supplier';
  }
  if (error.includes('Serial') || error.includes('serial')) {
    return 'Serial Number';
  }
  return 'Unknown';
}

/**
 * Validate a single asset row
 * Supports both old column names (Description, Bought Value, Bought Date) 
 * and new column names (Asset Name, Acquisition Cost, Acquisition Date)
 */
export function validateAssetRow(row: ParsedCSVRow): CSVValidationError[] {
  const errors: CSVValidationError[] = [];

  // Get values using both old and new column names for backward compatibility
  const assetCode = row['Asset Code'] || '';
  const assetName = row['Asset Name'] || row['Description'] || '';
  const category = row['Category'] || '';
  const location = row['Location'] || '';
  const acquisitionCost = row['Acquisition Cost'] || row['Bought Value'] || '';
  const acquisitionDate = row['Acquisition Date'] || row['Bought Date'] || '';

  // Validate Asset Code (required)
  if (!assetCode || assetCode.trim() === '') {
    errors.push({
      row: 0, // Will be set by caller
      field: 'Asset Code',
      error: 'Asset Code is required'
    });
  }

  // Validate Asset Name (required)
  if (!assetName || assetName.trim() === '') {
    errors.push({
      row: 0,
      field: 'Asset Name',
      error: 'Asset Name is required'
    });
  }

  // Validate Category (required)
  if (!category || category.trim() === '') {
    errors.push({
      row: 0,
      field: 'Category',
      error: 'Category is required'
    });
  }

  // Validate Location (required)
  if (!location || location.trim() === '') {
    errors.push({
      row: 0,
      field: 'Location',
      error: 'Location is required'
    });
  }

  // Validate Acquisition Cost (required)
  if (!acquisitionCost) {
    errors.push({
      row: 0,
      field: 'Acquisition Cost',
      error: 'Acquisition Cost is required'
    });
  } else {
    const value = parseFloat(acquisitionCost.toString().replace(/[£,]/g, ''));
    if (isNaN(value)) {
      errors.push({
        row: 0,
        field: 'Acquisition Cost',
        error: 'Invalid number format for Acquisition Cost'
      });
    } else if (value <= 0) {
      errors.push({
        row: 0,
        field: 'Acquisition Cost',
        error: 'Acquisition Cost must be greater than zero'
      });
    }
  }

  // Validate Acquisition Date (required)
  if (!acquisitionDate) {
    errors.push({
      row: 0,
      field: 'Acquisition Date',
      error: 'Acquisition Date is required'
    });
  } else {
    const date = parseDateString(acquisitionDate);
    if (!date || isNaN(date.getTime())) {
      errors.push({
        row: 0,
        field: 'Acquisition Date',
        error: 'Invalid date format. Use DD/MM/YYYY'
      });
    }
  }

  return errors;
}
